import divisions from "../data/nepal_administrative_divisions.json";

interface LocalLevelRecord {
  code: string;
  name_en: string;
  name_ne: string;
  type_en: string;
  type_ne: string;
}

interface DistrictRecord {
  code: string;
  name_en: string;
  name_ne: string;
  local_levels: LocalLevelRecord[];
}

interface ProvinceRecord {
  code: string;
  name_en: string;
  name_ne: string;
  districts: DistrictRecord[];
}

export interface NepalAddressValue {
  province: string;
  district: string;
  localLevel: string;
}

interface NepalAddressFieldsProps {
  value: NepalAddressValue;
  onChange: (value: NepalAddressValue) => void;
  required?: boolean;
  className?: string;
  localLevelLabel?: string;
}

const provinces: ProvinceRecord[] = divisions.provinces;

const comparable = (value: string) =>
  value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/\b(province|pradesh)\b/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "")
    .trim();

const matchesName = (value: string, english: string, nepali: string) => {
  const key = comparable(value);
  return Boolean(key) && (key === comparable(english) || key === comparable(nepali));
};

export const findNepalProvince = (value: string) =>
  provinces.find((province) => matchesName(value, province.name_en, province.name_ne));

export const findNepalDistrict = (provinceValue: string, districtValue: string) =>
  findNepalProvince(provinceValue)?.districts.find((district) =>
    matchesName(districtValue, district.name_en, district.name_ne),
  );

export const findNepalLocalLevel = (
  provinceValue: string,
  districtValue: string,
  localLevelValue: string,
) =>
  findNepalDistrict(provinceValue, districtValue)?.local_levels.find((localLevel) =>
    matchesName(localLevelValue, localLevel.name_en, localLevel.name_ne),
  );

export function NepalAddressFields({
  value,
  onChange,
  required = false,
  className = "form-row form-row--three",
  localLevelLabel = "Local level / municipality",
}: NepalAddressFieldsProps) {
  const selectedProvince = findNepalProvince(value.province);
  const districts = selectedProvince?.districts ?? [];
  const selectedDistrict = districts.find((district) =>
    matchesName(value.district, district.name_en, district.name_ne),
  );
  const localLevels = selectedDistrict?.local_levels ?? [];
  const provinceIsLegacy = Boolean(value.province) && !selectedProvince;
  const districtIsLegacy = Boolean(value.district) && !selectedDistrict;
  const localLevelIsLegacy =
    Boolean(value.localLevel) &&
    !localLevels.some((localLevel) =>
      matchesName(value.localLevel, localLevel.name_en, localLevel.name_ne),
    );

  return (
    <div className={className}>
      <label>
        <span>Province</span>
        <select
          required={required}
          autoComplete="address-level1"
          value={selectedProvince?.name_en ?? value.province}
          onChange={(event) => {
            onChange({ province: event.target.value, district: "", localLevel: "" });
          }}
        >
          <option value="">Select province</option>
          {provinceIsLegacy && <option value={value.province}>{value.province}</option>}
          {provinces.map((province) => (
            <option key={province.code} value={province.name_en}>
              {province.name_en} / {province.name_ne}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>District</span>
        <select
          required={required}
          disabled={!selectedProvince && !districtIsLegacy}
          value={selectedDistrict?.name_en ?? value.district}
          onChange={(event) => {
            onChange({ ...value, district: event.target.value, localLevel: "" });
          }}
        >
          <option value="">Select district</option>
          {districtIsLegacy && <option value={value.district}>{value.district}</option>}
          {districts.map((district) => (
            <option key={district.code} value={district.name_en}>
              {district.name_en} / {district.name_ne}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>{localLevelLabel}</span>
        <select
          required={required}
          disabled={!selectedDistrict && !localLevelIsLegacy}
          autoComplete="address-level2"
          value={value.localLevel}
          onChange={(event) => {
            onChange({ ...value, localLevel: event.target.value });
          }}
        >
          <option value="">Select local level</option>
          {localLevelIsLegacy && <option value={value.localLevel}>{value.localLevel}</option>}
          {localLevels.map((localLevel) => (
            <option key={localLevel.code} value={localLevel.name_en}>
              {localLevel.name_en} ({localLevel.type_en}) / {localLevel.name_ne}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
